import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { get } from 'lodash';

import LoadIcon from '../../assets/icons/load';
import { DOCUMENT_LIST_TYPE, LOANS_STATUS } from '../../constants/constants';
import FormFooter from '../../containers/form-footer';
import GridBorder from '../../modules/grid-border/grid-border';
import IconBtn from '../../modules/icon-btn/icon-btn';
import LabelValueItem from '../../modules/label-value-item/label-value-item';
import LoadDocBtn from '../../modules/load-doc-btn';
import ModuleTitleText from '../../modules/module-title-text/module-title-text';
import TableHeaderWrapper from '../../modules/table-header-wrapper/table-header-wrapper';
import TableWrapper from '../../modules/table-wrapper/table-wrapper';
import { CreditDetailsResponce } from '../../services/api/dto/Credits';
import { useAppDispatch } from '../../store';
import { closeCredit } from '../../store/actions/credit-details';
import { getCreditAgreement } from '../../store/actions/operations-sign';
import { setOperationResult } from '../../store/actions/sign-result';
import { RootState } from '../../types';
import currencyFormat from '../../utils/currencyFormat';
import { downloadFile, getPdfFromBase64 } from '../../utils/files/downloadFile';
import { getFormatDate } from '../../utils/utils';

import AssetsDetailsList from './assets-details-list/assets-details-list';
import AssetsDetailsTop from './assets-details-top/assets-details-top';

import './assets-credit-details.scss';

interface AssetsCreditDetailsProps {
  id: number;
  onClose: () => void;
  status: LOANS_STATUS;
  sum: number;
  deadlineAt: Date;
  info: {
    percent: number;
    appliedAt: Date;
    createdAt: Date;
    amount: number;
    delta: number;
  };
  docsList?: { name: string; type: DOCUMENT_LIST_TYPE; docId: number }[];
  asset: CreditDetailsResponce;
}

enum AssetsCreditDetailsStrings {
  HEADER_TITLE = 'Заём',
  OPERATION_TITLE = 'Полная стоимость:',
  DETAILED_INFO_TITLE = 'Информация по займу:',
  DOCS_TITLE = 'Подписанные документы:',
}

const AssetsCreditDetails: React.FC<AssetsCreditDetailsProps> = ({
  onClose,
  status,
  deadlineAt,
  sum,
  info,
  id,
  asset,
}) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const auth = useSelector((state: RootState) => state.auth.account);
  const companyInfo = useSelector(
    (state: RootState) => state.companiesInfo.companies
  );
  const accountId = useSelector((state: RootState) => state.auth.account.id);
  const balance = useSelector(
    (state: RootState) => state.balance.data.info?.simpleTokenValue
  );
  const loadingClose = useSelector(
    (state: RootState) => state.creditDetails.loading.close
  );
  const doc = useSelector(
    (state: RootState) => state.operationsSign.data.credit.decisionDoc
  );
  const [isValid, setValid] = useState(false);
  const isHistory = history.location.state?.isHistory;
  const isApplied = useMemo(() => status === 'APPLIED', [status]);
  const isClosed = useMemo(() => status === 'CLOSED', [status]);

  useEffect(() => {
    setValid(!!balance && sum <= balance);
  }, [balance]);

  useEffect(() => {
    dispatch(getCreditAgreement({ creditId: asset.id, type: 'RECEIVING' }));
  }, [asset.id]);

  const topDesc = useMemo(
    () =>
      `Заём ЦФА на сумму: ${currencyFormat(sum)} до ${getFormatDate(
        deadlineAt,
        undefined,
        true
      )}`,
    [sum, deadlineAt]
  );

  const detailsList = useMemo(
    () => [
      {
        title: 'Кредитор:',
        value: asset.creditor,
      },
      {
        title: 'Сумма займа:',
        value: currencyFormat(info.amount),
      },
      {
        title: 'Ставка:',
        value: `${info.percent} %`,
      },
      {
        title: 'Стоимость займа:',
        value: asset.paymentSum,
      },
      {
        title: 'Дата взятия:',
        value: getFormatDate(info.createdAt),
      },
      {
        title: 'Дата выплаты:',
        value: asset.paymentDate ? getFormatDate(asset.paymentDate) : '-',
      },
    ],
    [info]
  );

  const detailList = useMemo(
    () => [
      [
        {
          title: 'Заемщик:',
          value: get(companyInfo[auth.id], 'shortName', '-'),
        },
        {
          title: 'ИНН:',
          value: get(companyInfo[auth.id], 'tin', '-'),
        },
      ],
      [
        {
          title: 'Кредитор:',
          value: get(asset, 'creditor', '-'),
        },
        {
          title: 'ИНН:',
          value: get(asset, 'creditorTin', '-'),
        },
      ],
    ],
    [info]
  );

  const onCloseCredit = () => {
    dispatch(closeCredit({ id: asset.id, accountId, creditInfo: asset })).then(
      (action) => {
        const requestStatus = action.meta.requestStatus === 'fulfilled';
        dispatch(setOperationResult({ name: 'credit', status: requestStatus }));
        history.push(`/credit/details/${id}/repayment`);
      }
    );
  };

  const decisionDocs = useMemo(() => {
    const docs = [];

    if (doc) {
      docs.push(
        <LoadDocBtn
          title="Договор займа"
          onSpecDownload={() => {
            const resultBlob = getPdfFromBase64(doc);
            downloadFile(resultBlob, 'Договор займа.pdf', 'application/pdf');
          }}
        />
      );
    }
    return docs;
  }, [doc]);

  return (
    <TableWrapper>
      <div className="assets-details credit-details">
        <div className="assets-details__header">
          <TableHeaderWrapper
            // isHint
            hintPage="CREDIT_ASSET_DETAILS"
            leftContent={AssetsCreditDetailsStrings.HEADER_TITLE}
            onCloseClick={onClose}
          />
        </div>
        <div className="assets-details__body">
          <GridBorder
            infoTitle={
              <AssetsDetailsTop
                isHistory={isHistory}
                status={status}
                type="CREDIT"
                title={AssetsCreditDetailsStrings.OPERATION_TITLE}
                desc={topDesc}
              />
            }
            leftContent={
              <>
                <div className="assets-details__col credit-details__block">
                  <h2 className="text-2 assets-details__title">
                    Детали операции:
                  </h2>
                  <div className="credit-details__container">
                    {detailList.map((detail: any[]) => (
                      <div className="credit-details__col">
                        {detail.map(
                          (
                            d: { title: string; value: string | number },
                            index: any
                          ) => (
                            <div
                              className="assets-details__item"
                              key={`assets-details__item-${index}`}
                            >
                              <LabelValueItem
                                title={d.title || ''}
                                text={d.value || ''}
                              />
                            </div>
                          )
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                {!!decisionDocs?.length && (
                  <div className="assets-details__col credit-details__block">
                    <h2 className="text-2 assets-details__title">
                      {AssetsCreditDetailsStrings.DOCS_TITLE}
                    </h2>
                    {decisionDocs.map((decisionDoc, index) => (
                      <div
                        className="assets-details__item assets-details__item--mb-lg"
                        key={`assets-details__item-isDocsList-${index}`}
                      >
                        <div className="form-item">{decisionDoc}</div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            }
            rightContent={
              <AssetsDetailsList
                list={detailsList}
                title={AssetsCreditDetailsStrings.DETAILED_INFO_TITLE}
              />
            }
          />
        </div>
      </div>
      {((isApplied && !isHistory) ||
        (!isApplied && !isClosed && isHistory)) && (
        <FormFooter>
          <div className="form-assets__container">
            <div className="form-assets__flex">
              <div className="form-assets__item form-assets__item--sm">
                <ModuleTitleText
                  top="Полная стоимость займа:"
                  bottom={currencyFormat(sum)}
                />
              </div>
            </div>
            <div className="form-assets__item">
              <div className="form-item">
                <IconBtn
                  disabled={loadingClose || !isValid}
                  onClick={onCloseCredit}
                  icon={<LoadIcon type="SALE" color="white" />}
                  title="Погасить займ"
                />
              </div>
            </div>
          </div>
        </FormFooter>
      )}
    </TableWrapper>
  );
};

export default AssetsCreditDetails;
